import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, CalendarCheck, ArrowRight, Eyeglasses, Sparkle } from '@phosphor-icons/react';

const STORE_PHOTOS = [
  { src: '/api/static/branding/magasin-3.jpg', label: 'Le showroom' },
  { src: '/api/static/branding/magasin-1.jpg', label: 'La devanture' },
  { src: '/api/static/branding/magasin-4.jpg', label: 'Nos collections' },
  { src: '/api/static/branding/magasin-5.jpg', label: 'Espace essayage' },
  { src: '/api/static/branding/magasin-2.jpg', label: 'À Istres' },
];

const TEAM = [
  { name: 'Cyrille', role: 'Opticien diplômé', initials: 'C' },
  { name: 'Marielle', role: 'Opticienne', initials: 'M' },
  { name: 'Marilou', role: 'Opticienne — Contactologie', initials: 'M' },
  { name: 'Marie', role: 'Conseillère vente', initials: 'M' },
];

export const MrgStoreSections = ({ store, colors = {} }) => {
  const navigate = useNavigate();
  const primary = colors.primary || '#F9055B';
  const accent = colors.accent || '#FF004D';
  const id = store?.identity || {};
  const address = id.address || 'Centre Médical, 44 Chemin du Bord de Crau';
  const city = `${id.postal_code || '13800'} ${id.city || 'Istres'}`;
  const [activeImg, setActiveImg] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setActiveImg(i => (i + 1) % STORE_PHOTOS.length), 3500);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <>
      {/* ===== Notre boutique ===== */}
      <section id="notre-boutique" className="py-16 md:py-24 bg-white scroll-mt-24" data-testid="store-presentation">
        <div className="max-w-[1320px] mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <div className="rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] relative bg-slate-100">
              {STORE_PHOTOS.map((ph, i) => (
                <img key={i} src={ph.src} alt={`MRG Optique Istres — ${ph.label}`}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{ opacity: i === activeImg ? 1 : 0 }} loading="lazy" />
              ))}
              <span className="absolute bottom-3 left-3 z-10 px-3 py-1.5 rounded-full text-[12px] font-semibold text-white" style={{ backgroundColor: primary + 'CC' }}>{STORE_PHOTOS[activeImg].label}</span>
            </div>
            <div className="flex gap-2.5 mt-3">
              {STORE_PHOTOS.map((ph, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className="flex-1 rounded-xl overflow-hidden aspect-[4/3] transition-all" style={{ outline: i === activeImg ? `2px solid ${accent}` : '2px solid transparent', opacity: i === activeImg ? 1 : 0.55 }} data-testid={`store-thumb-${i}`}>
                  <img src={ph.src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
            <div className="absolute -top-4 -right-2 md:-right-4 bg-white rounded-2xl shadow-xl px-5 py-3 flex items-center gap-3 border border-slate-50">
              <span className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: accent }}><Eyeglasses size={20} weight="fill" /></span>
              <div>
                <div className="text-[18px] font-semibold leading-none" style={{ color: primary }}>+690</div>
                <div className="text-[11px] text-slate-500">modèles en boutique</div>
              </div>
            </div>
          </div>
          <div>
            <span className="text-[12px] font-bold uppercase tracking-[0.25em]" style={{ color: accent }}>Le magasin</span>
            <h2 className="text-[30px] md:text-[42px] font-semibold text-slate-900 leading-tight mt-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Votre opticien de proximité à Istres
            </h2>
            <p className="text-slate-600 text-[15.5px] leading-relaxed mt-4">
              Poussez la porte de MRG Optique &amp; Audition : un espace moderne où nos experts vous accompagnent dans le choix
              de vos lunettes solaires, optiques et lentilles. Essayages, conseils personnalisés et tiers payant mutuelles.
            </p>
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 text-slate-700"><span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: primary + '15', color: primary }}><MapPin size={18} weight="fill" /></span><span className="text-[14.5px]">{address}, {city}</span></div>
              <div className="flex items-center gap-3 text-slate-700"><span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: primary + '15', color: primary }}><Clock size={18} weight="fill" /></span><span className="text-[14.5px]">Lun–Sam : 9h–13h / 14h–19h</span></div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => navigate('/rendez-vous')} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-[14.5px] font-semibold transition-transform hover:scale-105" style={{ backgroundColor: primary }}>
                <CalendarCheck size={18} weight="bold" /> Prendre rendez-vous
              </button>
              <button onClick={() => navigate('/page/contact')} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[14.5px] font-bold border-2 transition-colors hover:bg-slate-50" style={{ borderColor: '#e5e7eb', color: '#1f2933' }}>
                Nous contacter <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Notre équipe ===== */}
      <section id="notre-equipe" className="py-16 md:py-20 scroll-mt-24" style={{ backgroundColor: '#faf7f8' }} data-testid="team-section">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[12px] font-bold uppercase tracking-[0.25em]" style={{ color: accent }}>Notre équipe</span>
            <h2 className="text-[30px] md:text-[42px] font-semibold text-slate-900 leading-tight mt-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Des experts à votre écoute</h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto text-[15px]">Une équipe passionnée et diplômée pour prendre soin de votre vue et de votre audition.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {TEAM.map((m, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center text-white text-[26px] font-semibold mb-4 relative"
                  style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}>
                  {m.initials}
                  {m.soon && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white shadow" style={{ color: accent }}>Bientôt</span>}
                </div>
                <h3 className="text-[16px] font-bold text-slate-900">{m.name}</h3>
                <p className="text-[12.5px] text-slate-500 mt-0.5">{m.role}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[12px] text-slate-400 mt-6 flex items-center justify-center gap-1.5">
            <Sparkle size={13} weight="fill" style={{ color: accent }} /> Photos et noms de l&apos;équipe personnalisables depuis le back-office
          </p>
        </div>
      </section>
    </>
  );
};

export default MrgStoreSections;
